import type { Result } from 'true-myth'

/**
 * Wire event name following the existing user:action:* convention
 * (user:action:identify, user:action:checkout, etc.).
 * Matches WAPI's genericUserEvent schema pattern and EventsController
 * Kafka routing (`event.startsWith('user')`).
 * See wanderu/messaging#57 for the consumer.
 */
export const EMAIL_SIGNUP_EVENT_NAME = 'user:action:email'

/**
 * Wire event name for an identify. Already admitted by WAPI's genericUserEvent
 * schema pattern, so no WAPI change is needed to send it.
 */
export const IDENTIFY_EVENT_NAME = 'user:action:identify'

/**
 * `source` for an identify fired because we detected an email-driven session.
 * Enumerated in messaging's IdentifyEventSchema alongside account_login /
 * account_update / booking — messaging rejects the event if this is absent
 * from that enum.
 */
export const EMAIL_CLICK_IDENTIFY_SOURCE = 'email_click'

/**
 * Producing-app identity for the `source` attribute.
 * Messaging's schema enumerates valid producers (nexus | ui-react | canopy).
 */
export const NEXUS_APP_SOURCE = 'nexus'

/**
 * Client-supplied Snowplow IDs read from cookies (_sp_id, sp).
 * All fields optional — cookies may not be set on first visit.
 * Use snake_case on the wire to match existing UserIDSchema
 * (see messaging/src/services/blueshift/eventSchemas.ts).
 */
export interface SnowplowIds {
    sp_domain_id?: string
    sp_network_id?: string
}

/**
 * The visitor's IP-derived location, in the nested camelCase shape messaging's
 * `UserLocationSchema` expects (unlike the flat snake_case of the rest of `data`). It
 * maps onto Blueshift's `locality` / `region` / `postal_code` / `country` / `ip` /
 * `latitude` / `longitude` profile attributes, which is what geo-segmentation of the
 * Content Subscribers journey reads.
 *
 * Every field optional: MaxMind resolves some IPs to a country and nothing finer.
 * `lat`/`lng` are nullable rather than absent because that is what ping returns for an
 * IP it cannot place.
 */
export interface WapiUserLocation {
    publicIp?: string
    city?: string
    state?: string
    country?: string
    postalCode?: string
    loc?: {
        lat?: number | null
        lng?: number | null
    }
}

/**
 * Payload for a `user:action:email` event.
 *
 * Shape matches WAPI's expected `{ event, event_timestamp, data }` envelope
 * and the messaging `NewsletterSignupEventSchema` field conventions
 * (sp_domain_id / sp_network_id / user_locale).
 *
 * WAPI auto-injects `context` from credentials — do NOT include it here.
 */
export interface WapiNewsletterSignupEvent {
    event: typeof EMAIL_SIGNUP_EVENT_NAME
    event_timestamp: number
    data: {
        email: string
        source: string
        signup_source: string
        user_locale: string
        sp_domain_id?: string
        sp_network_id?: string
        origin_city?: string
        destination_city?: string
        transport_mode?: string
        /**
         * Resolved in the visitor's browser and passed through by the endpoint — the
         * only place the visitor's own IP is visible, since the edge rewrites a
         * forwarded client-IP header with the connecting address. Absent when the
         * lookup failed: a signup is never lost over missing geo.
         */
        location?: WapiUserLocation
    }
}

/**
 * Payload for a `user:action:identify` event fired for an anonymous,
 * email-driven session.
 *
 * Deliberately carries **no** `email` and no `cognito_identity_id`: the whole
 * mechanism is that `sp_domain_id` reaches Blueshift as its `cookie`
 * identifier, and Blueshift stitches that cookie to the known recipient of the
 * email the visitor just clicked. Sending an address would defeat the design
 * and would leak PII through a route that requires no authentication.
 *
 * `sp_network_id` is accepted by messaging's schema but never forwarded to
 * Blueshift; it is sent for warehouse-side attribution only.
 *
 * WAPI auto-injects `context` from credentials — do NOT include it here.
 */
export interface WapiIdentifyEvent {
    event: typeof IDENTIFY_EVENT_NAME
    event_timestamp: number
    data: {
        source: typeof EMAIL_CLICK_IDENTIFY_SOURCE
        user_locale: string
        sp_domain_id?: string
        sp_network_id?: string
    }
}

/**
 * Every `user:action:*` event this app can POST through the general-purpose BFF
 * endpoint. Grows as a union; `WapiNewsletterSignupEvent` joins it when the
 * newsletter route is folded in.
 */
export type WapiUserEvent = WapiIdentifyEvent

export interface WapiEventsService {
    /**
     * POST a `user:action:email` event to WAPI /v2/events.json.
     * WAPI forwards to Kafka `user-events-{env}`; the messaging service
     * consumes and POSTs to Blueshift as `event: "email_signup"`.
     *
     */
    submitNewsletterSignup(event: WapiNewsletterSignupEvent): Promise<Result<void, Error>>

    /**
     * POST a `user:action:*` event to WAPI /v2/events.json.
     *
     * WAPI forwards anything named `user*` to Kafka `user-events-{env}`; messaging
     * consumes it and POSTs the mapped event to Blueshift. Generic on purpose — a
     * new `user:action:*` becomes a new member of `WapiUserEvent` rather than a new
     * service method.
     */
    submitUserEvent(event: WapiUserEvent): Promise<Result<void, Error>>
}
